import { ContentsApiResponse } from '~/searchApi';

/**
 * Helper function to merge original content with metrics data
 * Preserves all original fields while adding metrics data
 */
export const mergeContentWithMetrics = (
  originalContent: ContentsApiResponse['results'],
  metricsContent: ContentsApiResponse['results'],
) => {
  if (!metricsContent || metricsContent.length === 0) {
    return originalContent;
  }

  return originalContent.map((originalItem) => {
    const metricsItem = metricsContent.find(
      (item) => item.id === originalItem.id,
    );
    if (metricsItem) {
      // Merge the original content with metrics, preserving all original fields
      // Use type assertion to safely merge the objects
      return {
        ...originalItem,
        ...(metricsItem as any),
      };
    }
    return originalItem;
  });
};

/**
 * Merge only link-identifying fields from fetched content into originals.
 * - Instagram: shareUrl
 * - TikTok: externalUrl
 * - YouTube: networkId
 */
export const mergeContentWithLinks = (
  originalContent: ContentsApiResponse['results'],
  metricsContent: ContentsApiResponse['results'],
) => {
  if (!metricsContent || metricsContent.length === 0) {
    return originalContent;
  }

  return originalContent.map((originalItem) => {
    const metricsItem = metricsContent.find(
      (item) => item.id === originalItem.id,
    );
    if (!metricsItem) return originalItem;

    const platform =
      (metricsItem as any).platform || (originalItem as any).platform;

    if (platform === 'instagram') {
      const shareUrlSnake = (metricsItem as any).share_url;
      const shareUrlCamel = (metricsItem as any).shareUrl;
      // Prefer snake_case for API objects so downstream mappers can read it
      if (shareUrlSnake || shareUrlCamel) {
        return {
          ...(originalItem as any),
          share_url: shareUrlSnake ?? shareUrlCamel,
        };
      }
      return originalItem;
    }

    if (platform === 'tiktok') {
      const externalUrlSnake = (metricsItem as any).external_url;
      const externalUrlCamel = (metricsItem as any).externalUrl;
      if (externalUrlSnake || externalUrlCamel) {
        return {
          ...(originalItem as any),
          external_url: externalUrlSnake ?? externalUrlCamel,
        };
      }
      return originalItem;
    }

    if (platform === 'youtube') {
      const networkIdSnake = (metricsItem as any).network_id;
      const networkIdCamel = (metricsItem as any).networkId;
      if (networkIdSnake || networkIdCamel) {
        return {
          ...(originalItem as any),
          network_id: networkIdSnake ?? networkIdCamel,
        };
      }
      return originalItem;
    }

    return originalItem;
  });
};
